import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

// KPI surfaces, in the plain shadcn/Polar register.
//
// A KPI is a grey TILE: no border, no shadow, 16px radius, a muted label above a big regular
// number. The console used to render KPIs as a hairline-divided row of bare figures, which
// read as a ledger footer rather than as the four numbers the screen exists to state. Tiles
// are the shape every product console uses for this, and they survive being put next to a
// table card without either surface fighting the other.
//
// `StatStrip` keeps its name and its `{ label, value, delta }` item shape — eighteen screens
// pass it — and simply renders that data as a tile grid now, so all of them move together.

export type StatDelta = { value: string; tone: "up" | "down" | "neutral" }
export type StatItem = { label: string; value: ReactNode; delta?: StatDelta }

const deltaTone: Record<StatDelta["tone"], string> = {
  up: "text-success",
  down: "text-destructive",
  neutral: "text-muted-foreground",
}

/**
 * One grey KPI tile: label, hero figure, optional muted companion line.
 *
 * The figure is `font-normal`, not semibold. At 32px a regular weight is already the loudest
 * thing on the page; bolding it as well is what made the previous pass read as shouty.
 */
export function KpiTile({
  label,
  value,
  sub,
  subTone = "neutral",
  className,
}: {
  label: ReactNode
  value: ReactNode
  /** The muted companion figure ("EGP 184,500.00") or the reason there isn't one. */
  sub?: ReactNode
  subTone?: StatDelta["tone"]
  className?: string
}) {
  return (
    <div data-slot="kpi-tile" className={cn("min-w-0 rounded-lg bg-muted px-5 py-4", className)}>
      <div className="truncate text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-1.5 text-[32px] leading-none font-normal tracking-[-0.02em] tabular-nums">{value}</div>
      {sub ? <div className={cn("mt-2 truncate text-[13px] tabular-nums", deltaTone[subTone])}>{sub}</div> : null}
    </div>
  )
}

/**
 * `columns`
 *
 * `auto` (default) sizes the grid off the VIEWPORT; `compact` sizes it off the strip's own
 * box with a container query. The distinction is opt-in rather than global because the two
 * answer different questions: a media query is right for a strip that fills the page and
 * wrong for one inside a scaled design-graph frame or a narrow panel, where four cells with
 * EGP sub-figures silently stacked 2x2 at a nominal 960px.
 */
export type StatStripColumns = "auto" | "compact"

const GRID: Record<StatStripColumns, string> = {
  auto: "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4",
  compact: "grid grid-cols-1 gap-4 @min-[480px]:grid-cols-2 @min-[880px]:grid-cols-4",
}

export function StatStrip({
  items,
  className,
  columns = "auto",
}: {
  items: StatItem[]
  className?: string
  columns?: StatStripColumns
}) {
  const grid = (
    <div data-slot="stat-strip" className={cn(GRID[columns], columns === "auto" && className)}>
      {items.map((item, index) => (
        <KpiTile key={index} label={item.label} value={item.value} sub={item.delta?.value} subTone={item.delta?.tone} />
      ))}
    </div>
  )

  // The container element exists only in compact mode, so `auto` renders exactly the DOM it
  // rendered before this option existed.
  return columns === "compact" ? <div className={cn("@container", className)}>{grid}</div> : grid
}

/**
 * A white card that hosts hairline-divided panels — Polar's overview block.
 *
 * Distinct from a grid of tiles on purpose: tiles are four independent facts, a StatSection is
 * ONE subject cut into panels (a revenue chart plus its breakdowns, a set of source rows). The
 * dividers are the whole point, so the panels render flush and the card clips them.
 */
export function StatSection({
  title,
  actions,
  className,
  contentClassName,
  children,
}: {
  title?: ReactNode
  actions?: ReactNode
  className?: string
  contentClassName?: string
  children: ReactNode
}) {
  return (
    <section
      data-slot="stat-section"
      className={cn("overflow-hidden rounded-lg border border-border bg-card", className)}
    >
      {title || actions ? (
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          {title ? <h2 className="text-[15px] font-medium">{title}</h2> : <span />}
          {actions ? <div className="shrink-0">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cn("divide-y divide-border", contentClassName)}>{children}</div>
    </section>
  )
}
